import { kvDelete, kvGet, kvSet } from '@quickreport/sync';
import type { LocalDb } from './client';

/**
 * An offline PIN lets someone open LabView on this computer with no internet. It is checked
 * here, against a salted hash kept in the local database; the PIN itself is never stored. It
 * guards the screen, so it does not encrypt the saved data.
 */
const KEY = 'offline_pin';
const FAILS = 'pin_fails';
export const MIN_PIN = 4;
export const MAX_PIN = 12;
const ITERATIONS = 210_000;
const FREE_TRIES = 5;

type Q = Pick<LocalDb, 'query'>;

interface Stored {
  salt: string;
  hash: string;
  iterations: number;
}

const b64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function derive(pin: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
    key,
    256,
  );
  return new Uint8Array(bits);
}

export function pinProblem(pin: string): string | null {
  if (!/^\d+$/.test(pin)) return 'Use digits only.';
  if (pin.length < MIN_PIN || pin.length > MAX_PIN) return `Use ${MIN_PIN} to ${MAX_PIN} digits.`;
  if (/^(\d)\1+$/.test(pin) || '0123456789'.includes(pin) || '9876543210'.includes(pin))
    return 'Choose something less obvious than a run of the same or counting digits.';
  return null;
}

export async function hasPin(db: Q): Promise<boolean> {
  return (await kvGet(db, KEY)) !== null;
}

export async function setPin(db: Q, pin: string): Promise<void> {
  const problem = pinProblem(pin);
  if (problem) throw new Error(problem);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const stored: Stored = {
    salt: b64(salt),
    hash: b64(await derive(pin, salt, ITERATIONS)),
    iterations: ITERATIONS,
  };
  await kvSet(db, KEY, JSON.stringify(stored));
  await kvDelete(db, FAILS);
}

export async function clearPin(db: Q): Promise<void> {
  await kvDelete(db, KEY);
  await kvDelete(db, FAILS);
}

export type PinResult =
  | { ok: true }
  | { ok: false; reason: 'wrong'; triesLeft: number }
  | { ok: false; reason: 'wait'; seconds: number };

/** After five wrong tries each further one waits longer, so a stolen computer cannot be guessed at. */
export async function checkPin(db: Q, pin: string, now = Date.now()): Promise<PinResult> {
  const raw = await kvGet(db, KEY);
  if (!raw) return { ok: false, reason: 'wrong', triesLeft: 0 };
  const fails = JSON.parse((await kvGet(db, FAILS)) ?? '{"count":0,"until":0}') as {
    count: number;
    until: number;
  };
  if (fails.until > now) {
    return { ok: false, reason: 'wait', seconds: Math.ceil((fails.until - now) / 1000) };
  }
  const s = JSON.parse(raw) as Stored;
  const got = await derive(pin, unb64(s.salt), s.iterations);
  const want = unb64(s.hash);
  let diff = got.length ^ want.length;
  for (let i = 0; i < got.length; i++) diff |= (got[i] ?? 0) ^ (want[i] ?? 0);
  if (diff === 0) {
    await kvDelete(db, FAILS);
    return { ok: true };
  }
  const count = fails.count + 1;
  const over = count - FREE_TRIES;
  const until = over >= 0 ? now + Math.min(15 * 60_000, 15_000 * 2 ** over) : 0;
  await kvSet(db, FAILS, JSON.stringify({ count, until }));
  if (until > now) return { ok: false, reason: 'wait', seconds: Math.ceil((until - now) / 1000) };
  return { ok: false, reason: 'wrong', triesLeft: FREE_TRIES - count };
}
